import { Trans, useLingui } from "@lingui/react/macro";
import Chart from "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";
import { useEffect, useRef, useState } from "react";

import { apiGetEcgUuid } from "../../../api";
import { DefaultFullECGDataSchemaResponse } from "../../../api/generated/types.gen";
import { Button } from "../../../components/ui-kit/button";
import { useFetch } from "../../../utils/use-fetch";
import { parseDatetimeToLocal } from "../../../utils/utils";

// Register zoom plugin
Chart.register(zoomPlugin);

interface ECGPlotProps {
  uuid: string | null;
  onClose: () => void;
}

const colorPalette = [
  "rgba(187, 22, 163, 1)", // Pink/Magenta
  "rgba(5, 195, 222, 1)", // Cyan
  "rgba(0, 146, 203, 1)", // Blue
  "rgba(112, 115, 114, 1)", // Gray
  "rgba(37, 40, 42, 1)", // Dark gray
];

export function ECGPlot(props: ECGPlotProps) {
  const { t } = useLingui();
  const [ecgData, setEcgData] =
    useState<DefaultFullECGDataSchemaResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const { loading } = useFetch(async () => {
    if (!props.uuid) {
      setEcgData(null);
      setError(null);
      return;
    }

    try {
      setError(null);
      const response = await apiGetEcgUuid({
        path: { uuid: props.uuid },
      });
      setEcgData(response.data as DefaultFullECGDataSchemaResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : t`Failed to load ECG data`);
    }
  }, [props.uuid]);

  // Create/update chart when data changes
  useEffect(() => {
    // Destroy previous chart
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!ecgData || !canvasRef.current) {
      return;
    }

    if (!ecgData.ecg || !ecgData.leads || !ecgData.sampling_frequency) {
      return;
    }

    // Generate time axis (in seconds)
    const numSamples = ecgData.ecg[0]?.length || 0;
    const timePoints = Array.from(
      { length: numSamples },
      (_, i) => i / ecgData.sampling_frequency,
    );

    // Create datasets for each lead
    const datasets = ecgData.leads.map((lead, index) => ({
      label: lead,
      data: timePoints.map((tp, i) => ({ x: tp, y: ecgData.ecg[index][i] })),
      borderColor: colorPalette[index % colorPalette.length],
      backgroundColor: colorPalette[index % colorPalette.length],
      borderWidth: 1,
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 0,
    }));

    // Chart styling (light mode)
    const textColor = "rgba(17, 24, 39, 1)";
    const gridColor = "rgba(209, 213, 219, 0.5)";
    const backgroundColor = "rgba(255, 255, 255, 1)";

    // Create chart
    const ctx = canvasRef.current.getContext("2d");
    if (ctx) {
      // Add double-click handler to canvas
      canvasRef.current.ondblclick = () => {
        chartRef.current?.resetZoom();
      };

      chartRef.current = new Chart(ctx, {
        type: "line",
        data: { datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          backgroundColor: backgroundColor,
          plugins: {
            legend: {
              display: true,
              position: "top",
              labels: {
                usePointStyle: true,
                color: textColor,
              },
            },
            zoom: {
              zoom: {
                drag: {
                  enabled: true,
                },
                mode: "x",
              },
              pan: {
                enabled: true,
                mode: "x",
                modifierKey: "ctrl",
              },
              limits: {
                x: { min: "original", max: "original" },
                y: { min: "original", max: "original" },
              },
            },
            tooltip: {
              mode: "index",
              intersect: false,
              position: "nearest",
            },
          },
          scales: {
            x: {
              type: "linear",
              title: {
                display: true,
                text: t`time [s]`,
                font: {
                  size: 14,
                  weight: "bold",
                },
                color: textColor,
              },
              ticks: {
                color: textColor,
              },
              grid: {
                color: gridColor,
              },
            },
            y: {
              title: {
                display: true,
                text: t`amplitude [mV]`,
                font: {
                  size: 14,
                  weight: "bold",
                },
                color: textColor,
              },
              ticks: {
                color: textColor,
              },
              grid: {
                color: gridColor,
              },
            },
          },
        },
      });
    }

    // Cleanup on unmount
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [ecgData]);

  if (!props.uuid) {
    return null;
  }

  return (
    <div
      className="mb-6 rounded-lg border border-input bg-background p-4"
      style={{ maxWidth: "1200px" }}
    >
      <div className="mb-1 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">
            <Trans>ECG Plot</Trans>
            {ecgData?.measurement_datetime && (
              <>
                {" - "}
                {parseDatetimeToLocal(ecgData.measurement_datetime)}
              </>
            )}
          </h3>
          <div className="text-xs text-muted-foreground">
            <Trans>
              Click and drag to zoom, Ctrl+drag to pan, double-click to reset
            </Trans>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="subtle"
            onClick={() => chartRef.current?.resetZoom()}
          >
            <Trans>Reset Zoom</Trans>
          </Button>
          <Button variant="subtle" onClick={props.onClose}>
            <Trans>Close</Trans>
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex h-96 items-center justify-center">
          <div className="text-muted-foreground">
            <Trans>Loading ECG data...</Trans>
          </div>
        </div>
      )}

      {error && (
        <div className="flex h-96 items-center justify-center">
          <div className="text-danger-foreground">{error}</div>
        </div>
      )}

      {ecgData && !loading && !error && (
        <div className="h-96">
          <canvas ref={canvasRef} />
        </div>
      )}
    </div>
  );
}
